import { motion } from "framer-motion";
import { Users, Award, Calendar, Heart } from "lucide-react";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

const aboutJsonLd = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: "About DH Cleaning Services",
  description: "Learn about DH Cleaning Services — Melbourne's trusted cleaning team with 10+ years experience.",
  mainEntity: {
    "@type": "LocalBusiness",
    name: "DH Cleaning Services",
    foundingDate: "2014",
    numberOfEmployees: { "@type": "QuantitativeValue", minValue: 5, maxValue: 20 },
    areaServed: { "@type": "City", name: "Melbourne" },
    knowsAbout: ["Residential Cleaning", "Commercial Cleaning", "End of Lease Cleaning", "Carpet Cleaning", "Window Cleaning"],
  },
};

const stats = [
  { icon: Calendar, value: "10+", label: "Years Experience" },
  { icon: Users, value: "2,000+", label: "Happy Customers" },
  { icon: Award, value: "100%", label: "Satisfaction Guarantee" },
  { icon: Heart, value: "Eco", label: "Friendly Products" },
];

const team = [
  { name: "Daniel H.", role: "Founder & Director", initials: "DH" },
  { name: "Team Member", role: "Senior Cleaner", initials: "SC" },
  { name: "Team Member", role: "Operations", initials: "OP" },
];

const About = () => {
  return (
    <Layout>
      <SEO
        title="About DH Cleaning Melbourne"
        description="Learn about DH Cleaning Services — Melbourne's trusted cleaning team. 10+ years experience, 2,000+ happy customers, fully insured, eco-friendly products."
        canonical="/about"
        keywords="cleaning company Melbourne, about DH Cleaning, Melbourne cleaners, professional cleaning team"
        jsonLd={aboutJsonLd}
      />
      <section className="bg-gradient-to-br from-primary/10 via-background to-accent/5 py-16 lg:py-20">
        <div className="container text-center">
          <motion.div {...fadeUp}>
            <h1 className="font-display text-4xl sm:text-5xl font-bold text-foreground mb-4">About DH Cleaning Services</h1>
            <p className="text-lg text-muted-foreground max-w-lg mx-auto">
              Melbourne's trusted cleaning team — over 10 years of professional service.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story */}
      <section className="py-16 lg:py-24" aria-labelledby="story-heading">
        <div className="container max-w-3xl">
          <motion.div {...fadeUp}>
            <h2 id="story-heading" className="font-display text-3xl font-bold text-foreground mb-6">Our Story</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                DH Cleaning Services Pty Ltd was founded with a simple mission: to provide Melbourne homes and businesses with reliable, high-quality cleaning services at fair prices.
              </p>
              <p>
                What started as a small family operation has grown into a trusted team of dedicated cleaning professionals. We believe that a clean space is essential for well-being, productivity, and peace of mind.
              </p>
              <p>
                Every member of our team is fully trained, insured, and committed to delivering results that exceed expectations. We use eco-friendly products that are safe for your family, pets, and the environment — because caring for your space means caring for the planet too.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-muted/50" aria-labelledby="stats-heading">
        <div className="container">
          <h2 id="stats-heading" className="sr-only">Our achievements</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((s, i) => (
              <motion.div key={s.label} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.1 }} className="text-center">
                <div className="mx-auto mb-3 h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center">
                  <s.icon className="h-7 w-7 text-primary" aria-hidden="true" />
                </div>
                <div className="font-display text-3xl font-bold text-foreground">{s.value}</div>
                <div className="text-sm text-muted-foreground mt-1">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-16 lg:py-24" aria-labelledby="team-heading">
        <div className="container">
          <motion.div {...fadeUp} className="text-center mb-12">
            <h2 id="team-heading" className="font-display text-3xl font-bold text-foreground mb-3">Meet the Team</h2>
            <p className="text-muted-foreground">The people behind the sparkle.</p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-2xl mx-auto">
            {team.map((m, i) => (
              <motion.div key={i} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.1 }} className="text-center">
                <div className="mx-auto mb-4 h-24 w-24 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="font-display text-2xl font-bold text-primary">{m.initials}</span>
                </div>
                <h3 className="font-display font-semibold text-foreground">{m.name}</h3>
                <p className="text-sm text-muted-foreground">{m.role}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 lg:py-20 bg-primary text-primary-foreground" aria-labelledby="values-heading">
        <div className="container text-center max-w-2xl">
          <motion.div {...fadeUp}>
            <h2 id="values-heading" className="font-display text-3xl font-bold mb-6">Our Values</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-sm">
              {["Integrity & Trust", "Attention to Detail", "Customer First"].map((v) => (
                <div key={v} className="bg-primary-foreground/10 rounded-xl p-5">
                  <span className="font-display font-semibold text-base">{v}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default About;
